import { useEffect, useMemo, useState } from 'react';
import Lottie from 'lottie-react';

import {
  POKER_CARD_COUNT,
  POKER_HOLD_MS,
  POKER_STACK_TRANSITION_MS,
  POKER_SUIT_HOLD_FRAMES,
  POKER_TRANSITION_MS,
} from '@/features/LoadingScreen/constants/constants';
import { preparePokerSuitAnimation } from '@/features/LoadingScreen/helpers/preparePokerSuitAnimation';

import SpadeAnimation from '@/assets/animations/poker-suits/Spade.json';
import ClubAnimation from '@/assets/animations/poker-suits/Club.json';
import HeartAnimation from '@/assets/animations/poker-suits/Heart.json';
import DiamondAnimation from '@/assets/animations/poker-suits/Diamond.json';

import './styles/PokerSuitsAnimation.scss';

const createCardCycleTimers = (
  setFrontIndex: (fn: (index: number) => number) => void,
  setIsTransitioning: (value: boolean) => void,
) => {
  let holdTimer: ReturnType<typeof setTimeout>;
  let transitionTimer: ReturnType<typeof setTimeout>;

  const completeTransition = () => {
    setFrontIndex((index) => (index + 1) % POKER_CARD_COUNT);
    setIsTransitioning(false);
    scheduleHold();
  };

  const beginTransition = () => {
    setIsTransitioning(true);
    transitionTimer = setTimeout(completeTransition, POKER_TRANSITION_MS);
  };

  const scheduleHold = () => {
    holdTimer = setTimeout(beginTransition, POKER_HOLD_MS);
  };

  scheduleHold();

  return () => {
    clearTimeout(holdTimer);
    clearTimeout(transitionTimer);
  };
};

const SuitIcon = ({ data }: { data: object }) => (
  <div className="poker-suits-animation__lottie">
    <Lottie
      animationData={data}
      autoplay
      loop={false}
      style={{ width: '100%', height: '100%' }}
      rendererSettings={{
        preserveAspectRatio: 'xMidYMid meet',
      }}
    />
  </div>
);

export const PokerSuitsAnimation = () => {
  const suits = useMemo(
    () => [
      {
        id: 'spade',
        data: preparePokerSuitAnimation(
          SpadeAnimation,
          POKER_SUIT_HOLD_FRAMES.spade,
        ),
      },
      {
        id: 'club',
        data: preparePokerSuitAnimation(
          ClubAnimation,
          POKER_SUIT_HOLD_FRAMES.club,
        ),
      },
      {
        id: 'heart',
        data: preparePokerSuitAnimation(
          HeartAnimation,
          POKER_SUIT_HOLD_FRAMES.heart,
        ),
      },
      {
        id: 'diamond',
        data: preparePokerSuitAnimation(
          DiamondAnimation,
          POKER_SUIT_HOLD_FRAMES.diamond,
        ),
      },
    ],
    [],
  );

  const [frontIndex, setFrontIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => createCardCycleTimers(setFrontIndex, setIsTransitioning), []);

  const getStackOffset = (cardIndex: number) =>
    (cardIndex - frontIndex + POKER_CARD_COUNT) % POKER_CARD_COUNT;

  return (
    <div
      className="poker-suits-animation"
      style={{
        ['--transition-ms' as string]: `${POKER_TRANSITION_MS}ms`,
        ['--stack-transition-ms' as string]: `${POKER_STACK_TRANSITION_MS}ms`,
      }}
    >
      {suits.map((suit, index) => {
        const offset = getStackOffset(index);
        const isExiting = isTransitioning && offset === 0;
        const isPromoting = isTransitioning && offset === 1;

        return (
          <div
            key={suit.id}
            className={[
              'poker-suits-animation__card',
              `poker-suits-animation__card--offset-${offset}`,
              isExiting ? 'poker-suits-animation__card--exiting' : '',
              isPromoting ? 'poker-suits-animation__card--promoting' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid={`poker-suits-animation-card-${suit.id}`}
            aria-hidden={offset !== 0}
          >
            <div className="poker-suits-animation__face">
              <div className="poker-suits-animation__icon">
                <SuitIcon data={suit.data} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
